import {ComboboxItem} from "../components/_inputs/ha-combobox/combobox-item.interface";
import {IssueType} from "../domain/entities/issue-type.entity";
import {isAbsenceFamily, rootUidOf} from "../domain/helpers/issue-type-root.helper";

/**
 * The root an entry's type belongs to (D3): read from the IssueType the combobox carries as payload,
 * so a child counts as its root; an entry without a type payload counts by its own id.
 */
const rootOf = (item: ComboboxItem): string => rootUidOf(item.payload as IssueType | undefined) ?? String(item.id);

const TASKS_ITEM_FILTER = (item: ComboboxItem) => !isAbsenceFamily(rootOf(item));
const ABSENCES_ITEM_FILTER = (item: ComboboxItem) => isAbsenceFamily(rootOf(item));

/**
 * Extracted as a pure function (mirrors route-filters.ts / nav-item-filters.ts) so it's
 * directly unit-testable — the components that consume it (ha-tasklist.view.ts,
 * ha-form-issue-filters.component.ts) transitively pull in the app's full DI/browser-global
 * graph and aren't testable in place under vitest's node environment.
 *
 * Scopes the sidebar issue-type filter's SELECTABLE options to what a given tasklist mode
 * can actually contain — offering a type that the view structurally excludes would let a
 * user pick a filter that always yields zero results.
 *
 * Returns one of two module-level constant functions (not a freshly-built closure per call)
 * — ha-tasklist.view.ts passes the result straight into a Lit `.issueTypeItemFilter` property
 * binding, which dirty-checks by reference. A new closure on every call would make the child
 * component see a "changed" property on every parent re-render, not only on an actual mode
 * switch, since there are only ever two possible modes.
 */
export function buildTasklistIssueTypeItemFilter(mode: 'tasks' | 'absences'): (item: ComboboxItem) => boolean {
    return mode === 'absences' ? ABSENCES_ITEM_FILTER : TASKS_ITEM_FILTER;
}

/**
 * Whether a currently-set filter value (the selected IssueType, or its uid) is no longer allowed by
 * the given item filter —
 * e.g. a value persisted from a previous session, or set before switching between
 * /tasklist and /absence-list. Used to actively reset a now-invalid selection instead of
 * silently leaving it in place (in the UI or in localStorage).
 */
export function isFilterValueExcluded(
    current: IssueType | string | null | undefined,
    itemFilter: ((item: ComboboxItem) => boolean) | null
): boolean {
    if (!current || !itemFilter) {
        return false;
    }

    // The selected IssueType is passed as payload, so a child is judged by its root like the entries;
    // a bare uid carries no root and is judged by itself, which is right only for a root uid.
    return typeof current === 'string'
        ? !itemFilter({id: current, title: ''})
        : !itemFilter({id: current.uid, title: '', payload: current});
}
